import { useQuery } from '@apollo/client';
import SearchIcon from '@mui/icons-material/Search';
import Checkbox from '@mui/material/Checkbox';
import Chip, { ChipProps } from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormLabel from '@mui/material/FormLabel';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { SearchableTableLayout } from 'components/common/layout/searchable-table';
import { Table } from 'components/common/table';
import format from 'date-fns/format';
import { useNavTableConnection } from 'hooks/use-nav-table-connection';
import { useQueryParamState } from 'hooks/use-query-params';
import { FC } from 'react';
import { useParams } from 'react-router';
import {
  CampaignLeadLists as ICampaignLeadLists,
  CampaignLeadListsVariables,
} from 'types/apollo/CampaignLeadLists';
import { getTZ } from 'utils/date';
import { CampaignLeadListsQuery } from '../../_shared-queries/campaign-lead-lists.graphql';
import { applyFilter } from './filter';

export interface Data {
  createdAt: string;
  originalFilename: string;
  id: string;
  leadCount?: number | null;
  sentToCampaignTimestamp: string;
  status: string;
}

const ALL_STATUSES: Array<{ label: string; value: string; chipColor: ChipProps['color'] }> = [
  { label: 'Unprocessed', value: 'UNPROCESSED', chipColor: 'default' },
  { label: 'Processing', value: 'PROCESSING', chipColor: 'warning' },
  { label: 'Errored', value: 'ERRORED', chipColor: 'error' },
  { label: 'Processed', value: 'PROCESSED', chipColor: 'primary' },
  { label: 'Sent', value: 'SENT', chipColor: 'success' },
];

export const CampaignLeadLists: FC = () => {
  const { campaignId: id } = useParams();

  const [search, setSearch] = useQueryParamState('search', '');
  const [queryStatusFilter, setQueryStatusFilter] = useQueryParamState(
    'status',
    ALL_STATUSES.map(({ value }) => value).join(','),
  );
  const statusFilter = queryStatusFilter.split(',');
  const setStatusFilter = (statuses: string[]) => setQueryStatusFilter(statuses.join(','));

  const { data, loading } = useQuery<ICampaignLeadLists, CampaignLeadListsVariables>(
    CampaignLeadListsQuery,
    {
      variables: { campaignId: id ?? '0' },
    },
  );

  const tableData = useNavTableConnection<Data>([
    {
      id: 'originalFilename',
      label: 'File Name',
      getURL: ({ id }) => `${id}`,
    },
    {
      id: 'status',
      label: 'Status',
      renderer: (row) => <Status status={row.sentToCampaignTimestamp ? 'SENT' : row.status} />,
    },
    {
      id: 'leadCount',
      label: 'Lead Count',
      sortable: true,
      numeric: true,
      renderer: (row) => row.leadCount?.toLocaleString(),
    },
    {
      id: 'createdAt',
      label: 'Created',
      numeric: true,
      sortable: true,
      isDefaultSortColumn: true,
      renderer: (row) => (
        <Typography noWrap>
          {`${format(new Date(row.createdAt), 'yyyy-MM-dd hh:mm')} ${getTZ(
            new Date(row.createdAt),
          )}`}
        </Typography>
      ),
    },
  ]);

  const onStatusClick = (status: string) => () => {
    const idx = statusFilter.indexOf(status);
    if (idx >= 0) {
      const newStatusFilter = [...statusFilter];
      newStatusFilter.splice(idx, 1);
      setStatusFilter(newStatusFilter);
    } else {
      setStatusFilter([...statusFilter, status]);
    }
  };

  const tableRows = data?.campaign?.leadLists
    ? applyFilter(data.campaign.leadLists, { status: statusFilter, search })
    : [];

  return (
    <SearchableTableLayout>
      <SearchableTableLayout.FiltersWrapper>
        <TextField
          name="search"
          label="Search by File Name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <FormControl component="fieldset">
          <FormLabel component="legend">Status</FormLabel>
          <FormGroup>
            {ALL_STATUSES.map(({ label, value }) => {
              return (
                <FormControlLabel
                  key={value}
                  control={
                    <Checkbox
                      checked={statusFilter.includes(value)}
                      onClick={onStatusClick(value)}
                    />
                  }
                  label={label}
                />
              );
            })}
          </FormGroup>
        </FormControl>
      </SearchableTableLayout.FiltersWrapper>
      <SearchableTableLayout.TableWrapper>
        <Table<Data>
          loading={loading}
          rows={tableRows}
          page={tableData.page}
          rowsPerPage={tableData.rowsPerPage}
          order={tableData.order}
          orderBy={tableData.orderBy}
          onRequestSort={tableData.onRequestSort}
          setPage={tableData.setPage}
          setRowsPerPage={tableData.setRowsPerPage}
          columns={tableData.columns}
        />
      </SearchableTableLayout.TableWrapper>
    </SearchableTableLayout>
  );
};

const Status = ({ status }: { status: string }) => {
  const statusDef = ALL_STATUSES.find(({ value }) => value === status);
  if (statusDef) {
    return (
      <Chip
        size="small"
        label={statusDef.label}
        variant="outlined"
        color={statusDef.chipColor}
        sx={{ minWidth: '100px' }}
      />
    );
  }
  return null;
};
